import React from 'react';
import { ComponentNode } from '../types/template';

type EmptyStateKind = 'offers' | 'merchants' | 'products' | 'events' | 'promotions' | 'categories';

const EMPTY_STATE_COPY: Record<EmptyStateKind, { title: string; description: string }> = {
    offers: {
        title: 'No Active Offers Yet',
        description: "We're constantly updating our database. Check back soon for new savings!",
    },
    merchants: {
        title: 'No Merchants Yet',
        description: "We're always adding trusted brands. Check back soon!",
    },
    products: {
        title: 'No Products Yet',
        description: 'New products and deals will appear here soon.',
    },
    events: {
        title: 'No Events Yet',
        description: 'Check back soon for upcoming events and offers.',
    },
    promotions: {
        title: 'No Promotions Yet',
        description: 'Check back soon for the latest promotions.',
    },
    categories: {
        title: 'No Categories Yet',
        description: 'Check back soon for categories and offers.',
    },
};

export function getEmptyStateKind(component: ComponentNode): EmptyStateKind | undefined {
    switch (component.type) {
        case 'OffersSection':
        case 'OfferSection':
        case 'EventOfferSection':
        case 'TopDealsSection':
        case 'CombinedOffersSection':
            return 'offers';
        case 'MerchantSection':
        case 'MerchantsAlphabetSection':
            return 'merchants';
        case 'ProductsSection':
        case 'AllProductsSection':
            return 'products';
        case 'EventSection':
        case 'EventDealsSection':
            return 'events';
        case 'PromotionSection':
            return 'promotions';
        case 'CategorySection':
            return 'categories';
    }

    if (component.type !== 'SECTION') {
        return undefined;
    }

    const endpoint = String(component.apiEndpoint ?? component.apiConfig?.apiEndpoint ?? '').toLowerCase();
    if (endpoint.includes('event')) return 'events';
    if (endpoint.includes('promotion')) return 'promotions';
    if (endpoint.includes('product')) return 'products';
    if (endpoint.includes('merchant')) return 'merchants';
    if (endpoint.includes('offer') || endpoint.includes('banner')) return 'offers';
    return undefined;
}

interface EmptyStateComponentProps {
    kind: EmptyStateKind;
}

const EmptyStateComponent: React.FC<EmptyStateComponentProps> = ({ kind }) => {
    const { title, description } = EMPTY_STATE_COPY[kind];

    return (
        <div
            role="status"
            className="cb-empty-state"
            style={{
                boxSizing: 'border-box',
                display: 'flex',
                width: '100%',
                minHeight: '280px',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px 20px',
                border: '1px solid #eef0f3',
                borderRadius: '18px',
                backgroundColor: '#ffffff',
                textAlign: 'center',
            }}
        >
            <div
                aria-hidden="true"
                style={{
                    display: 'grid',
                    width: '64px',
                    height: '64px',
                    placeItems: 'center',
                    marginBottom: '18px',
                    borderRadius: '50%',
                    backgroundColor: '#f8f9fb',
                    color: '#e1e4e9',
                }}
            >
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13.2 2.5 5.7 13h5.1l-.8 8.5L18.3 11h-5.2l.1-8.5Z" />
                </svg>
            </div>
            <h2
                style={{
                    margin: 0,
                    color: '#182337',
                    fontSize: '18px',
                    fontWeight: 800,
                    lineHeight: 1.35,
                }}
            >
                {title}
            </h2>
            <p
                style={{
                    maxWidth: '360px',
                    margin: '6px 0 0',
                    color: '#8b95a7',
                    fontSize: '14px',
                    lineHeight: 1.5,
                }}
            >
                {description}
            </p>
        </div>
    );
};

export default EmptyStateComponent;
